/**
 * Files Service
 *
 * Handles file operations:
 * - Getting file metadata (HEAD request)
 * - Downloading files (GET request)
 */

import type { FileMetadataResponse } from '@/types';

/**
 * Get file metadata without downloading
 * @param contentUrl - File path from SPARQL query (e.g., '/data/files/example.railml')
 * @returns File metadata { size, lastModified, contentType }
 */
export async function getFileMetadata(contentUrl: string): Promise<FileMetadataResponse> {
  // Route through /download/ endpoint which is proxied by nginx
  const fullUrl = `/download${contentUrl}`;

  try {
    const response = await fetch(fullUrl, {
      method: 'HEAD',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to get file metadata: ${response.status} ${response.statusText}`);
    }

    const size = parseInt(response.headers.get('Content-Length') || '0', 10);
    const lastModified = response.headers.get('Last-Modified');
    const contentType = response.headers.get('Content-Type');

    return {
      size,
      lastModified: lastModified ? new Date(lastModified) : null,
      contentType
    };
  } catch (error) {
    console.error('[Files Service] Failed to get file metadata:', error);
    throw error;
  }
}

/**
 * Download a file with automatic UTF-8 BOM for text files (CSV, TTL, JSON-LD, etc.)
 * @param contentUrl - File path from SPARQL query (e.g., '/data/files/example.railml')
 * @param filename - Filename to save as
 */
export async function downloadFile(contentUrl: string, filename: string): Promise<void> {
  // Route through /download/ endpoint which is proxied by nginx
  const fullUrl = `/download${contentUrl}`;

  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
    }

    let blob = await response.blob();

    // For text-based files, ensure UTF-8 BOM encoding for proper Excel/app display
    const isTextFile = /\.(csv|ttl|rdf|jsonld|json|txt|xml)$/i.test(filename);
    if (isTextFile) {
      const text = await blob.text();
      const bom = '\uFEFF';
      const textWithBom = text.charCodeAt(0) === 0xFEFF ? text : bom + text;
      blob = new Blob([textWithBom], { type: 'text/plain;charset=utf-8' });
    }

    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log('[Files Service] Download successful:', filename);
  } catch (error) {
    console.error('[Files Service] Failed to download file:', error);
    throw error;
  }
}

/**
 * Format file size for display
 * @param bytes - File size in bytes (number or string)
 * @returns Formatted size (e.g., "2.5 MB") or '-' if not available
 */
export function formatFileSize(bytes: string | number | null | undefined): string {
  if (!bytes || bytes === 0) return '-';

  const numBytes = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  if (isNaN(numBytes) || numBytes === 0) return '-';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(numBytes) / Math.log(k));
  const value = numBytes / Math.pow(k, i);

  const decimals = value >= 10 ? 1 : 2;
  return `${value.toFixed(decimals)} ${sizes[i]}`;
}

/**
 * Download multiple files as a single ZIP archive.
 * @param files - Array of files with contentUrl and title
 * @param zipName - Filename for the resulting zip (e.g. 'railml-files.zip')
 */
export async function downloadFilesAsZip(
  files: { contentUrl: string; title: string }[],
  zipName: string,
): Promise<void> {
  const { createZipBlob } = await import('@utils/zip.utils');

  const entries = await Promise.all(
    files.map(async (file) => {
      const fullUrl = `/download${file.contentUrl}`;
      const response = await fetch(fullUrl, { method: 'GET', credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Failed to download "${file.title}": ${response.status} ${response.statusText}`);
      }
      const buffer = await response.arrayBuffer();
      const filename = file.contentUrl.split('/').pop() || file.title;
      return { name: filename, data: new Uint8Array(buffer) };
    }),
  );

  const blob = createZipBlob(entries);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = zipName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default {
  getFileMetadata,
  downloadFile,
  downloadFilesAsZip,
  formatFileSize,
};
